/* eslint-disable immutable/no-mutation */

import { isPanel } from '@hooks/usePanel';
import { appRoutes } from '@utils/consts';
import { parseRoute } from '@utils/i18n';
import { localStore } from '@utils/localStore';
import { formatBody, formatResponse } from '@utils/textHelpers';

export type ResponseError = Error & {
  info: Record<string, unknown>;
  status: number;
  url: string;
};

type ResponseHeaders = Pick<Headers, 'has' | 'get'>;

const saveUserAuthParams = (headers: ResponseHeaders, panel?: boolean, impersonate?: boolean) => {
  const key = `app-${panel && !impersonate ? 'admin' : 'user'}`;
  if (!headers.has('Access-Token')) return;
  const auth: Record<string, string | null> = {};
  const oldData = localStore.getItem(key);
  let cache: Record<string, string> = {};
  if (oldData && typeof oldData === 'string') cache = JSON.parse(oldData) as Record<string, string>;
  if (headers.has('Access-Token')) {
    auth['Access-Token'] = headers.get('Access-Token') || cache['Access-Token'];
  }
  if (headers.has('Client')) auth.Client = headers.get('Client') || cache.Client;
  if (headers.has('Uid')) auth.Uid = headers.get('Uid') || cache.Uid;
  if (headers.has('Expiry')) {
    auth.Expiry = headers.get('Expiry')?.toString() || cache.Expiry;
  }
  const expiry = parseInt(auth.Expiry || cache.Expiry, 10);
  localStore.updateItem(key, auth, expiry * 1000 - Date.now());
};

const UNAUTHORIZED_IGNORED_URLS = [/(^|\/)sign_in$/, /^sign_in_exchanges\/[^/]+\/redeem$/];

const handleUnauthorized = (status: number, url: string) => {
  if (status !== 401 || UNAUTHORIZED_IGNORED_URLS.some((pattern) => pattern.test(url))) return;
  localStore.removeItem('app-user');
  localStore.removeItem('app-foreman');
  localStore.removeItem('app-admin');
  localStore.removeItem('app-impersonate');
  localStore.removeItem('app-start-impersonate');
  const path = parseRoute(isPanel() ? `/${appRoutes.panel}` : appRoutes.index);
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  window.location.href = `${cleanPath}?expired=true`;
};

const getUserAuthParams = (panel?: boolean) => {
  const key = `app-${panel ? 'admin' : 'user'}`;
  const authHeaders: HeadersInit = {};
  const savedData = localStore.getItem(key);
  let cache: Record<string, string> = {};
  if (savedData) {
    if (typeof savedData === 'string') cache = JSON.parse(savedData) as Record<string, string>;
    authHeaders['Access-Token'] = cache['Access-Token'];
  }
  authHeaders.Client = cache.Client;
  authHeaders.Uid = cache.Uid;
  authHeaders.Expiry = cache.Expiry;
  return authHeaders;
};

const REQUEST_TIMEOUT_MS = 30 * 1000;

const getUrl = (mock: boolean, panel: boolean) => {
  if (mock) return import.meta.env.VITE_MOCK_URL as string;
  if (panel) return import.meta.env.VITE_PANEL_API_URL as string;
  return import.meta.env.VITE_API_URL as string;
};

export const queryFetch = async <T>({
  url,
  data,
  opt,
  mock = false,
  auth = true,
  panel = false,
  impersonate = false,
  formData,
  blob,
  timeout = REQUEST_TIMEOUT_MS,
}: {
  url: string;
  data?: Record<string, unknown>;
  opt?: RequestInit;
  mock?: boolean;
  auth?: boolean;
  panel?: boolean;
  impersonate?: boolean;
  formData?: boolean;
  blob?: boolean;
  timeout?: number;
}): Promise<T> => {
  const createOptions = () => {
    const options: RequestInit = mock
      ? {}
      : {
          headers: {
            'Access-Control-Allow-Origin': '*',
            'Cache-Control': 'no-store',
            'Access-Control-Max-Age': '7200',
          },
          cache: 'reload',
        };
    options.method = opt?.method || 'GET';
    if (!blob && !formData) options.signal = AbortSignal.timeout(timeout);

    if (!formData) options.headers = { ...options.headers, 'Content-Type': 'application/json' };
    if (data) options.body = JSON.stringify(formatBody(data));
    if (opt?.body) options.body = opt.body;
    if (auth && !mock) {
      options.headers = {
        ...options.headers,
        ...getUserAuthParams(panel),
      };
    }
    return options;
  };

  const api = getUrl(mock, panel);
  const resp = await fetch([api, url].join('/'), createOptions());

  handleUnauthorized(resp.status, url);
  if (resp.status !== 401) saveUserAuthParams(resp.headers, panel, impersonate);

  if (blob) return resp as T;

  if (!resp.ok && (opt?.method || 'GET') === 'GET') {
    const info = (formatResponse(await resp.json().catch(() => ({}))) || {}) as Record<
      string,
      unknown
    >;
    throw Object.assign(new Error(`Request failed with status ${resp.status}`), {
      info,
      status: resp.status,
      url,
      errors: Array.isArray(info.errors) ? info.errors : [],
    }) as ResponseError;
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return formatResponse(await resp.json());
};

export const uploadFetch = <T>({
  url,
  method = 'POST',
  body,
  panel = false,
  impersonate = false,
  onProgress,
}: {
  url: string;
  method?: string;
  body: FormData;
  panel?: boolean;
  impersonate?: boolean;
  onProgress?: (percent: number) => void;
}): Promise<T> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const authHeaders = getUserAuthParams(panel) as Record<string, string>;

    xhr.open(method, [getUrl(false, panel), url].join('/'));
    xhr.setRequestHeader('Cache-Control', 'no-store');
    Object.entries(authHeaders).forEach(([name, value]) => {
      if (value) xhr.setRequestHeader(name, value);
    });

    xhr.upload.onprogress = (event) => {
      if (!onProgress || !event.lengthComputable) return;
      onProgress(Math.round((event.loaded / event.total) * 100));
    };

    xhr.onload = () => {
      handleUnauthorized(xhr.status, url);
      if (xhr.status !== 401) {
        saveUserAuthParams(
          {
            has: (name) => xhr.getResponseHeader(name) !== null,
            get: (name) => xhr.getResponseHeader(name),
          },
          panel,
          impersonate,
        );
      }
      try {
        resolve(formatResponse(JSON.parse(xhr.responseText || '{}')) as T);
      } catch (err) {
        reject(err as Error);
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(body);
  });
